import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { SearchBarProps } from '../types';

const SearchBar = ({ searchQuery, onSearchChange, suggestions }: SearchBarProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const filteredSuggestions = (Array.isArray(suggestions) ? suggestions : [])
    .filter(suggestion =>
      typeof suggestion === 'string' &&
      suggestion.toLowerCase().includes((searchQuery || '').toLowerCase())
    ).slice(0, 5);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearchChange(value);
    setShowSuggestions(value.length > 0);
    setActiveSuggestionIndex(-1);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSearchChange(suggestion);
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (activeSuggestionIndex >= 0) {
          handleSuggestionClick(filteredSuggestions[activeSuggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
        break;
    }
  };

  const clearSearch = () => {
    onSearchChange('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      <div className="relative flex items-center">
        <div className="absolute left-4 z-10">
          <Icon name="Search" size={18} className="text-[#0C2C39]/40" />
        </div>

        <input
          ref={inputRef}
          type="text"
          placeholder="Search projects, technologies, or keywords..."
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(searchQuery.length > 0)}
          className="w-full bg-transparent pl-12 pr-12 py-4 text-sm font-bold text-[#0C2C39] placeholder-[#0C2C39]/30 focus:outline-none"
        />

        {searchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-4 p-2 hover:bg-[#0C2C39]/5 rounded-lg transition-colors text-[#0C2C39]/40"
          >
            <Icon name="X" size={16} />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute top-[calc(100%+12px)] left-0 right-0 bg-white border-2 border-[#0C2C39] rounded-2xl shadow-[8px_8px_0px_0px_#0C2C39] z-[9999] overflow-hidden"
        >
          <div className="p-2">
            <p className="px-3 py-2 text-[10px] font-black text-[#0C2C39]/30 uppercase tracking-widest">
              Suggested Matches
            </p>
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center gap-3 ${index === activeSuggestionIndex
                  ? 'bg-[#0C2C39] text-white'
                  : 'text-[#0C2C39] hover:bg-[#0C2C39]/5'
                  }`}
              >
                <Icon
                  name="Search"
                  size={14}
                  className={index === activeSuggestionIndex ? 'text-white/40' : 'text-[#0C2C39]/20'}
                />
                <span className="text-xs font-bold">{suggestion}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;